/**
 * Taška★ bag-in control — distinct from SP ★ (FavoriteStarIcon) and green + cart add.
 * Screen 5 ★ stays SP-only on BrowseNearbySalesPointCard.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BagStarIcon } from '../../shared/ui/BagStarIcon.js';
import { CUSTOMER_MIN_TOUCH_TARGET_CLASS } from '../../shared/ui/customerTouchTarget.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { showToast } from '../../shared/ui/toastStore.js';
import {
  isOfferFavoriteMembershipBlockedError,
  isOfferFavoriteNotListableError,
  setOfferFavorite,
} from '../account/offerFavoritesApi.js';
import { stagePendingOfferFavorite } from '../account/pendingOfferFavorites.js';
import { usePersonalOfferFavoriteKeys } from '../discovery/usePersonalOfferFavoriteKeys.js';
import { resolveDiscoveryOfferDisplayImageUrl } from './browseOfferPresentation.js';
import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';

export type OfferBagInButtonProps = {
  readonly offer: PublicDiscoveryOfferItemDTO;
  readonly testId: string;
  readonly className?: string;
};

export function OfferBagInButton(props: OfferBagInButtonProps): JSX.Element {
  const { offer, testId, className } = props;
  const { t } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const signedIn = isGloballySignedInChrome({ session });
  const { hasGrain } = usePersonalOfferFavoriteKeys();
  const [pending, setPending] = useState(false);

  const grain = {
    salesPointId: offer.salesPointContext.salesPointId,
    productId: offer.productContext.productId,
    variantId: offer.productContext.variantId,
  };
  const inBag = hasGrain(grain);

  const handleClick = async (): Promise<void> => {
    if (inBag || pending) {
      return;
    }
    if (!signedIn) {
      stagePendingOfferFavorite({
        tenantCode: offer.tenantContext.tenantCode,
        salesPointId: offer.salesPointContext.salesPointId,
        salesPointSlug: offer.salesPointContext.code,
        productId: offer.productContext.productId,
        variantId: offer.productContext.variantId,
        title: offer.title,
        name: offer.title,
        imageUrl: resolveDiscoveryOfferDisplayImageUrl(offer),
        thumbnailUrl: offer.thumbnailUrl,
        price: offer.price,
        salesPointName: offer.salesPointContext.name,
        salesPointCode: offer.salesPointContext.code,
        tenantId: offer.tenantContext.tenantId,
        tenantName: offer.tenantContext.tenantName,
      });
      showToast({
        message: t('browse.discovery.bagInToast', {
          defaultValue: 'Přidáno do Tašky★',
        }),
        variant: 'success',
      });
      return;
    }
    setPending(true);
    try {
      await setOfferFavorite({
        apiBaseUrl,
        body: {
          salesPointId: offer.salesPointContext.salesPointId,
          productId: offer.productContext.productId,
          variantId: offer.productContext.variantId,
          favorite: true,
        },
      });
      const customerKey = session?.customerId ?? 'session';
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.offerFavorites(customerKey),
      });
      showToast({
        message: t('browse.discovery.bagInToast', {
          defaultValue: 'Přidáno do Tašky★',
        }),
        variant: 'success',
      });
    } catch (err) {
      if (isOfferFavoriteMembershipBlockedError(err)) {
        return;
      }
      if (isOfferFavoriteNotListableError(err)) {
        showToast({
          message: t('browse.discovery.bagNotListableToast', {
            defaultValue: 'Tuto nabídku nelze přidat do Tašky★.',
          }),
          variant: 'danger',
        });
        return;
      }
      showToast({
        message: t('browse.discovery.bagInErrorToast', {
          defaultValue: 'Tašku★ se nepodařilo aktualizovat. Zkuste to znovu.',
        }),
        variant: 'danger',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      className={[
        'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-transparent bg-transparent',
        'hover:bg-[var(--color-surface-muted)]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'focus-visible:outline-[var(--color-focus-ring)]',
        'disabled:cursor-not-allowed disabled:opacity-[0.55]',
        inBag
          ? 'text-[var(--color-warning)]'
          : 'text-[var(--color-on-surface-muted)]',
        CUSTOMER_MIN_TOUCH_TARGET_CLASS,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
      title={
        inBag
          ? t('browse.discovery.bagInActiveAria', {
              defaultValue: 'V Tašce★: {{name}}',
              name: offer.title,
            })
          : t('browse.discovery.bagInAria', {
              defaultValue: 'Přidat do Tašky★: {{name}}',
              name: offer.title,
            })
      }
      aria-label={
        inBag
          ? t('browse.discovery.bagInActiveAria', {
              defaultValue: 'V Tašce★: {{name}}',
              name: offer.title,
            })
          : t('browse.discovery.bagInAria', {
              defaultValue: 'Přidat do Tašky★: {{name}}',
              name: offer.title,
            })
      }
      aria-pressed={inBag}
      disabled={pending || inBag}
      onClick={(event) => {
        event.stopPropagation();
        void handleClick();
      }}
    >
      <BagStarIcon filled={inBag} />
    </button>
  );
}
